/**
 * Calculates the match confidence meter state based on form completion
 * and answered questions.
 */

import MatchConfidenceLevel from '../Data/MatchConfidenceLevel';
import { shouldShowComponent } from './conditionality';
import {
  isPostcodeComponent,
  isComponentAnswered,
  getQuestionComponents,
} from '../Components/__helpers__/component.helpers';
import { ComponentTypes } from '../Data/Components';

/**
 * Calculate form completion progress (0-100)
 * Based on visible questions answered
 * @param {Object} components - Components from Redux store
 * @param {string} dynamicNewsletterName - Dynamic newsletter name (optional)
 * @param {string} phoneNumberCountryCode - Phone number country code (optional)
 * @param {string} subcategoryId - Subcategory ID (optional)
 * @returns {number} Progress from 0-100
 */
export const calculateProgress = (
  components,
  dynamicNewsletterName = null,
  phoneNumberCountryCode = null,
  subcategoryId = null
) => {
  const allQuestionComponents = getQuestionComponents(components);

  if (!allQuestionComponents?.length) {
    return 0;
  }

  // Filter to only include visible questions (excludes conditionally hidden questions)
  const visibleQuestionComponents = allQuestionComponents.filter(
    ({ component }) =>
      shouldShowComponent(
        components,
        component,
        dynamicNewsletterName,
        phoneNumberCountryCode,
        subcategoryId
      )
  );

  if (!visibleQuestionComponents?.length) {
    return 0;
  }

  // Exclude supplier-match, newsletter-signup, and appointment-booking from progress calculation
  // supplier-card is already excluded by ComponentsWithoutCustomerInput via getQuestionComponents
  const componentsToCount = visibleQuestionComponents.filter(
    ({ component }) => {
      const componentType = component?.component?.type;
      return (
        componentType !== ComponentTypes.SUPPLIER_MATCH &&
        componentType !== ComponentTypes.NEWSLETTER_SIGNUP &&
        componentType !== ComponentTypes.APPOINTMENT_BOOKING
      );
    }
  );

  if (!componentsToCount?.length) {
    return 0;
  }

  const answeredCount = componentsToCount.filter(({ component }) =>
    isComponentAnswered(component)
  ).length;

  return Math.round((answeredCount / componentsToCount.length) * 100);
};

/**
 * Calculate confidence level based on answered questions
 * @param {Object} components - Components from Redux store
 * @param {string} dynamicNewsletterName - Dynamic newsletter name (optional)
 * @param {string} phoneNumberCountryCode - Phone number country code (optional)
 * @param {string} subcategoryId - Subcategory ID (optional)
 * @returns {string} Confidence level: '-', 'fair', 'medium', or 'high'
 */
export const calculateConfidenceLevel = (
  components,
  dynamicNewsletterName = null,
  phoneNumberCountryCode = null,
  subcategoryId = null
) => {
  const allQuestionComponents = getQuestionComponents(components);

  if (!allQuestionComponents?.length) return MatchConfidenceLevel.NULL;

  // Filter to only include visible questions (excludes conditionally hidden questions)
  // This ensures the halfway point is calculated based on questions the user actually sees
  const visibleQuestionComponents = allQuestionComponents.filter(
    ({ component }) =>
      shouldShowComponent(
        components,
        component,
        dynamicNewsletterName,
        phoneNumberCountryCode,
        subcategoryId
      )
  );

  if (!visibleQuestionComponents?.length) return MatchConfidenceLevel.NULL;

  const answeredComponents = visibleQuestionComponents.filter(({ component }) =>
    isComponentAnswered(component)
  );

  // No questions answered
  if (!answeredComponents?.length) {
    return MatchConfidenceLevel.NULL;
  }

  // Find the index of the postcode question in the VISIBLE questions array
  const postcodeIndex = visibleQuestionComponents.findIndex(({ component }) =>
    isPostcodeComponent(component)
  );

  // Check if postcode is answered
  const hasAnsweredPostcode = answeredComponents.some(({ component }) =>
    isPostcodeComponent(component)
  );

  if (hasAnsweredPostcode) {
    return MatchConfidenceLevel.HIGH;
  }

  // If there's no postcode question, use simple logic
  if (postcodeIndex === -1) {
    // One question answered = fair
    if (answeredComponents.length === 1) {
      return MatchConfidenceLevel.FAIR;
    }
    // More than one = medium
    return MatchConfidenceLevel.MEDIUM;
  }

  // Skip "fair" level if postcode is the first question (index 0)
  const isPostcodeFirstQuestion = postcodeIndex === 0;

  // Skip "medium" level if postcode is the second or third question (index 1 or 2)
  // With only 1-2 questions before postcode, go straight from FAIR to HIGH
  const shouldSkipMedium = postcodeIndex <= 2;

  // Count answered questions that are NOT the postcode question
  const answeredNonPostcodeCount = answeredComponents.filter(
    ({ component }) => !isPostcodeComponent(component)
  ).length;

  // If postcode is first question, skip fair and medium levels
  // Go straight from null to high (when postcode is answered)
  if (isPostcodeFirstQuestion) {
    return MatchConfidenceLevel.NULL;
  }

  // MEDIUM: At least 50% of questions between the first question and the upcoming Postcode/Zip question have been answered
  // Gets skipped if Postcode/Zip is the second or third question (1-2 questions before postcode)
  if (!shouldSkipMedium && answeredNonPostcodeCount >= 1) {
    // Need to answer 50% of questions before postcode
    const questionsBeforePostcode = postcodeIndex;
    const halfwayCount = Math.ceil(questionsBeforePostcode / 2);

    if (answeredNonPostcodeCount >= halfwayCount) {
      return MatchConfidenceLevel.MEDIUM;
    }
  }

  // FAIR: At least one question has been answered that is NOT the postcode/Zip question
  // Gets skipped if Postcode/Zip is the first question (already handled above)
  if (answeredNonPostcodeCount >= 1) {
    return MatchConfidenceLevel.FAIR;
  }

  // Default to null if no conditions met
  return MatchConfidenceLevel.NULL;
};
